import { motion } from "framer-motion";
import YearSelect from "./YearSelect";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut, Pie } from "react-chartjs-2";
import { buildSlices, formatCount, formatMoney, formatPercent, metricValue } from "./dashboardData";

ChartJS.register(ArcElement, Tooltip, Legend);

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      titleColor: "white",
      bodyColor: "white",
      borderColor: "rgba(255, 255, 255, 0.1)",
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
    },
  },
};

const SliceLegend = ({ slices, formatValue }) => {
  if (slices.empty) {
    return <p className="text-sm text-gray-500">No data</p>;
  }

  return (
    <div className="space-y-2">
      {slices.rows.map((row, index) => (
        <div key={`${row.label}-${index}`} className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center">
            <div
              className="mr-3 h-3 w-3 flex-shrink-0 rounded-full"
              style={{ backgroundColor: slices.colors[index] }}
            />
            <span className="truncate text-sm text-gray-700">{row.label}</span>
          </div>
          <span className="text-sm font-medium text-gray-800">
            {formatValue(row)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function SalesRevenueOverview({ year, setYear, data, loading }) {
  const currency = data?.total_revenue?.currency || "GHS";
  const policySlices = buildSlices(data?.sales_by_policy_type, "label", "count");
  const locationSlices = buildSlices(data?.top_locations, "location", "count");
  const caption = loading ? "Loading..." : `For ${year}`;

  const kpiData = [
    { title: "Total Sales", value: loading ? "..." : formatCount(metricValue(data?.total_sales)) },
    { title: "Total Revenue", value: loading ? "..." : formatMoney(metricValue(data?.total_revenue), currency) },
    { title: "Revenue Growth Rate", value: loading ? "..." : formatPercent(metricValue(data?.revenue_growth_rate)) },
    { title: "Returning Customers Rate", value: loading ? "..." : formatPercent(metricValue(data?.returning_customers_rate)) },
    { title: "New Customers", value: loading ? "..." : formatCount(metricValue(data?.new_customers)) },
  ];

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <motion.h1
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="text-2xl font-bold text-gray-800"
        >
          Sales & Revenue Overview
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <YearSelect
            year={year}
            onChange={setYear}
            className="border-green-300 focus:border-green-400 focus:ring-green-200"
          />
        </motion.div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5">
        {kpiData.map((kpi, index) => (
          <motion.div
            key={kpi.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
            className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md"
          >
            <h3 className="mb-3 text-sm font-medium text-gray-600">{kpi.title}</h3>
            <div className="text-2xl font-bold text-gray-800">{kpi.value}</div>
            <p className="mt-2 text-xs text-gray-400">{caption}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7 }}
        className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2"
      >
        <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
          <h2 className="mb-6 text-lg font-semibold text-gray-800">Sales by Policy Type</h2>
          <div className="mb-6 flex items-center justify-center">
            <div className="relative h-64 w-64">
              <Doughnut
                data={{
                  labels: policySlices.labels,
                  datasets: [{ data: policySlices.values, backgroundColor: policySlices.colors, borderWidth: 0, cutout: "60%" }],
                }}
                options={chartOptions}
              />
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-2xl font-bold text-gray-800">
                  {policySlices.empty ? "---" : formatCount(policySlices.total)}
                </div>
                <div className="text-sm text-gray-600">Sales</div>
              </div>
            </div>
          </div>
          <SliceLegend slices={policySlices} formatValue={(row) => formatCount(row.value)} />
        </div>

        <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
          <h2 className="mb-6 text-lg font-semibold text-gray-800">Top Location of Purchase</h2>
          <div className="mb-6 flex items-center justify-center">
            <div className="relative h-64 w-64">
              <Pie
                data={{
                  labels: locationSlices.labels,
                  datasets: [{ data: locationSlices.values, backgroundColor: locationSlices.colors, borderWidth: 0 }],
                }}
                options={chartOptions}
              />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="text-lg font-bold text-gray-800">
                  {locationSlices.empty ? "---" : formatCount(locationSlices.total)}
                </div>
              </div>
            </div>
          </div>
          <SliceLegend slices={locationSlices} formatValue={(row) => formatCount(row.value)} />
        </div>
      </motion.div>
    </div>
  );
}
